import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ApiSettingsTab from './components/ApiSettingsTab';
import BrandingTab from './components/BrandingTab';
import UserPreferencesTab from './components/UserPreferencesTab';
import SystemConfigTab from './components/SystemConfigTab';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('api');

  const tabs = [
    {
      id: 'api',
      label: 'API Settings',
      icon: 'Key',
      description: 'Manage OCR service API keys and usage monitoring'
    },
    {
      id: 'branding',
      label: 'Branding',
      icon: 'Palette',
      description: 'Customize company information and visual appearance'
    },
    {
      id: 'preferences',
      label: 'User Preferences',
      icon: 'Settings',
      description: 'Configure personal settings and defaults'
    },
    {
      id: 'system',
      label: 'System Config',
      icon: 'Cog',
      description: 'Advanced system configuration and integration settings'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'api':
        return <ApiSettingsTab />;
      case 'branding':
        return <BrandingTab />;
      case 'preferences':
        return <UserPreferencesTab />;
      case 'system':
        return <SystemConfigTab />;
      default:
        return <ApiSettingsTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Settings - Invoice Parser Pro</title>
        <meta name="description" content="Configure system settings, API keys, branding, and user preferences for Invoice Parser Pro" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground">
                  Configure system settings, API keys, and preferences
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-4 sticky top-24">
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-smooth
                        ${activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon 
                        name={tab?.icon} 
                        size={20} 
                        className={activeTab === tab?.id ? 'text-primary-foreground' : 'text-muted-foreground'}
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${
                          activeTab === tab?.id ? 'text-primary-foreground' : 'text-foreground'
                        }`}>
                          {tab?.label}
                        </div>
                        <div className={`text-xs mt-1 ${
                          activeTab === tab?.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                        }`}>
                          {tab?.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>

                {/* Quick Actions */}
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-sm font-medium text-foreground mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full flex items-center space-x-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-smooth">
                      <Icon name="Download" size={16} />
                      <span>Export Settings</span>
                    </button>
                    <button className="w-full flex items-center space-x-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-smooth">
                      <Icon name="Upload" size={16} />
                      <span>Import Settings</span>
                    </button>
                    <button className="w-full flex items-center space-x-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-smooth">
                      <Icon name="RotateCcw" size={16} />
                      <span>Reset All</span>
                    </button>
                  </div>
                </div>

                {/* System Status */}
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-sm font-medium text-foreground mb-3">System Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">API Status</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="text-xs text-success">Online</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Storage</span>
                      <span className="text-xs text-foreground">2.4GB / 10GB</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Last Backup</span>
                      <span className="text-xs text-foreground">2 hours ago</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg">
                {/* Tab Header */}
                <div className="border-b border-border p-6">
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={tabs?.find(tab => tab?.id === activeTab)?.icon || 'Settings'} 
                      size={24} 
                      className="text-primary" 
                    />
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">
                        {tabs?.find(tab => tab?.id === activeTab)?.label}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {tabs?.find(tab => tab?.id === activeTab)?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;